import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calculator, Droplets, Package, Target, Mail, Phone, Share2, Calendar, ArrowRight, X, RotateCcw, Minus, Plus, Sliders } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';

// Product mineral data (per liter) - actual values with correct product images
const products = [
    {
        name: 'ROI',
        origin: 'Slovenia',
        magnesium: 1300,
        calcium: 610,
        sodium: 1600,
        potassium: 17,
        bicarbonate: 7700,
        tds: 7481,
        bottleSize: 0.65,
        price: 18.99,
        color: '#1A3C34',
        image: 'https://customer-assets.emergentagent.com/job_raw-water-market/artifacts/pd35iedd_ROI%20bottle.webp',
        productId: 'roi',
        strengths: ['sleep', 'stress', 'muscle', 'recovery', 'concentration', 'energy']
    },
    {
        name: 'Gerolsteiner',
        origin: 'Germany',
        magnesium: 108,
        calcium: 348,
        sodium: 118,
        potassium: 11,
        bicarbonate: 1816,
        tds: 2500,
        bottleSize: 0.75,
        price: 8.99,
        color: '#2E5C55',
        image: 'https://customer-assets.emergentagent.com/job_raw-water-market/artifacts/ot3qnepg_gerolsteiner-sparkling-glas-0-75-fl-usa.png',
        productId: 'gerolsteiner',
        strengths: ['boneHealth', 'injury', 'muscle', 'endurance']
    },
    {
        name: 'Vichy Catalan',
        origin: 'Spain',
        magnesium: 9.4,
        calcium: 32,
        sodium: 1097,
        potassium: 49,
        bicarbonate: 2120,
        tds: 2900,
        bottleSize: 1,
        price: 12.99,
        color: '#44403C',
        image: 'https://customer-assets.emergentagent.com/job_raw-water-market/artifacts/wc8yyxxj_Vichy%20Catalan%20bottle.png',
        productId: 'vichy-catalan',
        strengths: ['digestion', 'recovery', 'endurance', 'energy']
    },
    {
        name: 'Marzia',
        origin: 'Italy',
        magnesium: 100,
        calcium: 315,
        sodium: 13,
        potassium: 1.2,
        bicarbonate: 571,
        tds: 1372,
        bottleSize: 0.75,
        price: 9.49,
        color: '#57534E',
        image: 'https://customer-assets.emergentagent.com/job_raw-water-market/artifacts/vr69mse0_marzia-naturale.webp',
        productId: 'marzia',
        strengths: ['boneHealth', 'muscle', 'sleep', 'stress', 'concentration']
    }
];

// Health focus options with their mineral associations
// Health focus options with mineral associations and product boosts
// Boosts are calculated OBJECTIVELY based on actual mineral content ratios
// Normalized to scale 0-3 where highest content = 3
//
// Mineral content per liter:
// ROI: Mg 1300, Ca 610, HCO3 7700, Na 1600
// Gerolsteiner: Mg 108, Ca 348, HCO3 1816, Na 118
// Vichy Catalan: Mg 9.4, Ca 32, HCO3 2120, Na 1097
// Marzia: Mg 100, Ca 315, HCO3 571, Na 13
//
const healthFocusOptions = [
    // Magnesium-focused (ROI dominant: 1300 vs 108/100/9.4)
    { id: 'sleep', minerals: ['magnesium'], boost: { roi: 3, gerolsteiner: 1, marzia: 1 } },
    { id: 'stress', minerals: ['magnesium'], boost: { roi: 3, gerolsteiner: 1, marzia: 1 } },
    { id: 'concentration', minerals: ['magnesium'], boost: { roi: 3, gerolsteiner: 1, marzia: 1 } },
    
    // Bicarbonate-focused - Vichy prioritized (2120 > Gerol 1816), ROI also high (7700)
    { id: 'digestion', minerals: ['bicarbonate'], boost: { vichy: 3, roi: 2, gerolsteiner: 1 } },
    
    // Magnesium + Bicarbonate (recovery, energy)
    { id: 'recovery', minerals: ['magnesium', 'bicarbonate'], boost: { roi: 2, vichy: 2, gerolsteiner: 1 } },
    { id: 'energy', minerals: ['magnesium', 'bicarbonate'], boost: { roi: 2, vichy: 2, gerolsteiner: 1 } },
    
    // Electrolytes - Na+K (Vichy and ROI both high in sodium)
    { id: 'endurance', minerals: ['sodium', 'potassium', 'bicarbonate'], boost: { vichy: 3, roi: 2, gerolsteiner: 1 } },
    
    // Calcium + Magnesium (ROI: 610+1300, Gerol: 348+108, Marzia: 315+100)
    { id: 'muscle', minerals: ['magnesium', 'calcium'], boost: { roi: 3, gerolsteiner: 1, marzia: 1 } },
    { id: 'injury', minerals: ['calcium', 'magnesium'], boost: { roi: 3, gerolsteiner: 2, marzia: 2 } },
    { id: 'boneHealth', minerals: ['calcium', 'magnesium'], boost: { roi: 3, gerolsteiner: 2, marzia: 2 } }
];

const MineralCalculator = () => {
    const { t, language } = useLanguage();
    const [formData, setFormData] = useState({
        age: '',
        gender: '',
        weight: '',
        activityLevel: '',
        lowSodium: false,
        healthFocus: []
    });
    const [results, setResults] = useState(null);
    const [excludedProducts, setExcludedProducts] = useState([]);
    const [customTargets, setCustomTargets] = useState(null); // For interactive mineral adjustment
    const [adjustedPlan, setAdjustedPlan] = useState(null); // Adjusted bottle counts based on custom targets

    const toggleHealthFocus = (focusId) => {
        setFormData(prev => ({
            ...prev,
            healthFocus: prev.healthFocus.includes(focusId)
                ? prev.healthFocus.filter(f => f !== focusId)
                : [...prev.healthFocus, focusId]
        }));
    };

    const calculateNeeds = () => {
        const { age, gender, weight, activityLevel } = formData;
        
        if (!age || !gender || !weight || !activityLevel) return;

        // WHO/FAO Based mineral needs (mg/day)
        let baseMagnesium = gender === 'male' ? 400 : 310;
        let baseCalcium = gender === 'male' ? 1000 : 1000;
        let baseSodium = 1500;
        let basePotassium = 3510;
        let baseBicarbonate = 1500;

        // Age adjustments
        const ageNum = parseInt(age);
        if (ageNum > 50) {
            baseCalcium = 1200;
            baseMagnesium += 20;
        }
        if (ageNum > 70) {
            baseMagnesium += 20;
            baseCalcium = 1300;
        }

        // Activity level multipliers
        const activityMultipliers = {
            sedentary: 1.0,
            moderate: 1.15,
            active: 1.3,
            veryActive: 1.5
        };
        const multiplier = activityMultipliers[activityLevel];

        // Weight adjustment
        const weightNum = parseInt(weight);
        const weightFactor = 0.7 + (weightNum / 70) * 0.3;

        // Calculate daily needs
        const dailyNeeds = {
            magnesium: Math.round(baseMagnesium * multiplier * weightFactor),
            calcium: Math.round(baseCalcium * multiplier * weightFactor),
            sodium: Math.round(baseSodium * multiplier),
            potassium: Math.round(basePotassium * multiplier),
            bicarbonate: Math.round(baseBicarbonate * multiplier)
        };

        // Weekly needs
        const weeklyNeeds = {
            magnesium: dailyNeeds.magnesium * 7,
            calcium: dailyNeeds.calcium * 7,
            sodium: dailyNeeds.sodium * 7,
            potassium: dailyNeeds.potassium * 7,
            bicarbonate: dailyNeeds.bicarbonate * 7
        };

        // Calculate optimal combination
        const combination = calculateOptimalCombination(weeklyNeeds, formData.lowSodium, formData.healthFocus);

        setResults({
            dailyNeeds,
            weeklyNeeds,
            combination,
            lowSodium: formData.lowSodium,
            healthFocus: formData.healthFocus
        });
    };

    const calculateOptimalCombination = (weeklyNeeds, lowSodium = false, healthFocus = []) => {
        // Base distribution (7 bottles total)
        let bottles = {
            roi: 1,
            gerolsteiner: 2,
            vichy: 2,
            marzia: 2
        };

        // Adjust based on health focus selections
        if (healthFocus.length > 0) {
            // Reset to base
            bottles = { roi: 1, gerolsteiner: 1, vichy: 1, marzia: 1 };
            let remaining = 3; // 7 - 4 base bottles

            // Count boosts for each product
            let boosts = { roi: 0, gerolsteiner: 0, vichy: 0, marzia: 0 };
            
            healthFocus.forEach(focusId => {
                const focus = healthFocusOptions.find(f => f.id === focusId);
                if (focus && focus.boost) {
                    if (focus.boost.roi) boosts.roi += focus.boost.roi;
                    if (focus.boost.gerolsteiner) boosts.gerolsteiner += focus.boost.gerolsteiner;
                    if (focus.boost.vichy) boosts.vichy += focus.boost.vichy;
                    if (focus.boost.marzia) boosts.marzia += focus.boost.marzia;
                }
            });

            // Distribute remaining bottles based on boosts
            const totalBoosts = boosts.roi + boosts.gerolsteiner + boosts.vichy + boosts.marzia;
            if (totalBoosts > 0) {
                const roiExtra = Math.round((boosts.roi / totalBoosts) * remaining);
                const gerolsteinerExtra = Math.round((boosts.gerolsteiner / totalBoosts) * remaining);
                const vichyExtra = Math.round((boosts.vichy / totalBoosts) * remaining);
                const marziaExtra = remaining - roiExtra - gerolsteinerExtra - vichyExtra;

                bottles.roi += roiExtra;
                bottles.gerolsteiner += gerolsteinerExtra;
                bottles.vichy += vichyExtra;
                bottles.marzia += Math.max(0, marziaExtra);
            } else {
                // Default distribution if no boosts
                bottles.gerolsteiner += 1;
                bottles.vichy += 1;
                bottles.marzia += 1;
            }
        }

        // Apply low sodium adjustment
        if (lowSodium) {
            // Move bottles from high-sodium to low-sodium waters
            if (bottles.vichy > 1) {
                bottles.vichy -= 1;
                bottles.marzia += 1;
            }
            if (bottles.roi > 1) {
                bottles.roi -= 1;
                bottles.gerolsteiner += 1;
            }
        }

        // Ensure total is 7
        let total = bottles.roi + bottles.gerolsteiner + bottles.vichy + bottles.marzia;
        while (total > 7) {
            if (bottles.marzia > 1) { bottles.marzia--; total--; }
            else if (bottles.gerolsteiner > 1) { bottles.gerolsteiner--; total--; }
            else if (bottles.vichy > 1) { bottles.vichy--; total--; }
            else if (bottles.roi > 1) { bottles.roi--; total--; }
        }
        while (total < 7) {
            bottles.marzia++;
            total++;
        }

        // Build weekly plan with bilingual roles
        const roleTranslations = {
            'Magnesium-boost': { no: 'Magnesium-boost', en: 'Magnesium boost' },
            'Kalsium-kilde': { no: 'Kalsium-kilde', en: 'Calcium source' },
            'Fordøyelse': { no: 'Fordøyelse', en: 'Digestion' },
            'Daglig hydrering': { no: 'Daglig hydrering', en: 'Daily hydration' }
        };
        
        const weeklyPlan = [
            { ...products[0], bottles: bottles.roi, role: roleTranslations['Magnesium-boost'] },
            { ...products[1], bottles: bottles.gerolsteiner, role: roleTranslations['Kalsium-kilde'] },
            { ...products[2], bottles: bottles.vichy, role: roleTranslations['Fordøyelse'] },
            { ...products[3], bottles: bottles.marzia, role: roleTranslations['Daglig hydrering'] }
        ];

        // Calculate provided minerals
        let totalProvided = {
            magnesium: 0,
            calcium: 0,
            sodium: 0,
            potassium: 0,
            bicarbonate: 0
        };

        weeklyPlan.forEach(item => {
            const liters = item.bottles * item.bottleSize;
            totalProvided.magnesium += item.magnesium * liters;
            totalProvided.calcium += item.calcium * liters;
            totalProvided.sodium += item.sodium * liters;
            totalProvided.potassium += item.potassium * liters;
            totalProvided.bicarbonate += item.bicarbonate * liters;
        });

        // Calculate coverage
        const coverage = {
            magnesium: Math.min(100, Math.round((totalProvided.magnesium / weeklyNeeds.magnesium) * 100)),
            calcium: Math.min(100, Math.round((totalProvided.calcium / weeklyNeeds.calcium) * 100)),
            sodium: Math.min(100, Math.round((totalProvided.sodium / weeklyNeeds.sodium) * 100)),
            potassium: Math.min(100, Math.round((totalProvided.potassium / weeklyNeeds.potassium) * 100)),
            bicarbonate: Math.min(100, Math.round((totalProvided.bicarbonate / weeklyNeeds.bicarbonate) * 100))
        };

        const avgCoverage = Math.round(
            (coverage.magnesium + coverage.calcium + coverage.sodium + coverage.potassium + coverage.bicarbonate) / 5
        );

        return {
            plan: weeklyPlan,
            totalProvided,
            coverage,
            avgCoverage,
            totalBottles: weeklyPlan.reduce((sum, item) => sum + item.bottles, 0)
        };
    };

    // Function to remove a product and redistribute bottles to maintain mineral coverage
    const removeProductAndRedistribute = (productNameToRemove) => {
        if (!results) return;
        
        // Add to excluded list
        setExcludedProducts(prev => [...prev, productNameToRemove]);
        
        // Get the removed product's mineral contribution
        const removedProduct = results.combination.plan.find(p => p.name === productNameToRemove);
        if (!removedProduct || removedProduct.bottles === 0) return;
        
        const removedMinerals = {
            magnesium: removedProduct.magnesium * removedProduct.bottles * removedProduct.bottleSize,
            calcium: removedProduct.calcium * removedProduct.bottles * removedProduct.bottleSize,
            sodium: removedProduct.sodium * removedProduct.bottles * removedProduct.bottleSize,
            potassium: removedProduct.potassium * removedProduct.bottles * removedProduct.bottleSize,
            bicarbonate: removedProduct.bicarbonate * removedProduct.bottles * removedProduct.bottleSize
        };
        
        // Get remaining products that are not excluded
        const remainingProducts = results.combination.plan.filter(p => 
            p.name !== productNameToRemove && !excludedProducts.includes(p.name) && !p.removed
        );
        
        if (remainingProducts.length === 0) return;
        
        // Get the relevant minerals based on selected health focus areas
        const relevantMinerals = new Set();
        formData.healthFocus.forEach(focusId => {
            const focus = healthFocusOptions.find(f => f.id === focusId);
            if (focus) {
                focus.minerals.forEach(m => relevantMinerals.add(m));
            }
        });
        
        // If no health focus selected, use the main mineral from removed product
        let priorityMinerals = Array.from(relevantMinerals);
        if (priorityMinerals.length === 0) {
            const mainMineral = Object.entries(removedMinerals)
                .sort((a, b) => b[1] - a[1])[0][0];
            priorityMinerals = [mainMineral];
        }
        
        // Find the best replacement based on health focus boosts first, then mineral content
        let bestReplacement = null;
        let bestScore = -1;
        
        remainingProducts.forEach(product => {
            let score = 0;
            
            // First priority: Check health focus boosts for this product
            formData.healthFocus.forEach(focusId => {
                const focus = healthFocusOptions.find(f => f.id === focusId);
                if (focus && focus.boost) {
                    const productKey = product.name.toLowerCase().replace(' ', '');
                    // Map product names to boost keys
                    const boostKey = product.name === 'ROI' ? 'roi' : 
                                    product.name === 'Gerolsteiner' ? 'gerolsteiner' :
                                    product.name === 'Vichy Catalan' ? 'vichy' :
                                    product.name === 'Marzia' ? 'marzia' : null;
                    if (boostKey && focus.boost[boostKey]) {
                        score += focus.boost[boostKey] * 100; // Weight boosts heavily
                    }
                }
            });
            
            // Second priority: Mineral content for relevant minerals
            priorityMinerals.forEach(mineral => {
                score += (product[mineral] || 0) * product.bottleSize;
            });
            
            if (score > bestScore) {
                bestScore = score;
                bestReplacement = product;
            }
        });
        
        if (!bestReplacement) {
            bestReplacement = remainingProducts[0];
        }
        
        // Calculate extra bottles needed based on the primary mineral
        const primaryMineral = priorityMinerals[0] || 'magnesium';
        const targetMineralAmount = removedMinerals[primaryMineral] || 0;
        const mineralPerBottle = (bestReplacement[primaryMineral] || 0) * bestReplacement.bottleSize;
        const extraBottles = mineralPerBottle > 0 ? Math.ceil(targetMineralAmount / mineralPerBottle) : 0;
        
        // Update the results with redistributed bottles
        const newPlan = results.combination.plan.map(p => {
            if (p.name === productNameToRemove) {
                return { ...p, bottles: 0, removed: true };
            }
            if (p.name === bestReplacement.name) {
                return { ...p, bottles: p.bottles + extraBottles };
            }
            return p;
        });
        
        // Recalculate totals
        let newTotalProvided = {
            magnesium: 0, calcium: 0, sodium: 0, potassium: 0, bicarbonate: 0
        };
        
        newPlan.forEach(item => {
            if (!item.removed) {
                const liters = item.bottles * item.bottleSize;
                newTotalProvided.magnesium += item.magnesium * liters;
                newTotalProvided.calcium += item.calcium * liters;
                newTotalProvided.sodium += item.sodium * liters;
                newTotalProvided.potassium += item.potassium * liters;
                newTotalProvided.bicarbonate += item.bicarbonate * liters;
            }
        });
        
        const weeklyNeeds = results.weeklyNeeds;
        const newCoverage = {
            magnesium: Math.min(100, Math.round((newTotalProvided.magnesium / weeklyNeeds.magnesium) * 100)),
            calcium: Math.min(100, Math.round((newTotalProvided.calcium / weeklyNeeds.calcium) * 100)),
            sodium: Math.min(100, Math.round((newTotalProvided.sodium / weeklyNeeds.sodium) * 100)),
            potassium: Math.min(100, Math.round((newTotalProvided.potassium / weeklyNeeds.potassium) * 100)),
            bicarbonate: Math.min(100, Math.round((newTotalProvided.bicarbonate / weeklyNeeds.bicarbonate) * 100))
        };
        
        const newAvgCoverage = Math.round(
            (newCoverage.magnesium + newCoverage.calcium + newCoverage.sodium + newCoverage.potassium + newCoverage.bicarbonate) / 5
        );
        
        setResults({
            ...results,
            combination: {
                ...results.combination,
                plan: newPlan,
                totalProvided: newTotalProvided,
                coverage: newCoverage,
                avgCoverage: newAvgCoverage,
                totalBottles: newPlan.filter(p => !p.removed).reduce((sum, item) => sum + item.bottles, 0)
            }
        });
    };

    // Function to restore a removed product
    const restoreProduct = (productName) => {
        setExcludedProducts(prev => prev.filter(p => p !== productName));
        // Recalculate from scratch
        calculateNeeds();
    };

    // Initialize custom targets when results change
    useEffect(() => {
        if (results && results.combination) {
            setCustomTargets({ ...results.combination.coverage });
            setAdjustedPlan(null);
        }
    }, [results?.combination?.avgCoverage]);

    // Function to calculate adjusted bottle plan based on custom mineral targets
    const calculateAdjustedPlan = (newTargets) => {
        if (!results || !results.weeklyNeeds) return;

        // Get available products (not excluded)
        const availableProducts = products.filter(p => 
            !excludedProducts.includes(p.name) && 
            !results.combination.plan.find(item => item.name === p.name && item.removed)
        );

        if (availableProducts.length === 0) return;

        // Calculate required mineral amounts based on target percentages
        const requiredMinerals = {
            magnesium: (newTargets.magnesium / 100) * results.weeklyNeeds.magnesium,
            calcium: (newTargets.calcium / 100) * results.weeklyNeeds.calcium,
            sodium: (newTargets.sodium / 100) * results.weeklyNeeds.sodium,
            potassium: (newTargets.potassium / 100) * results.weeklyNeeds.potassium,
            bicarbonate: (newTargets.bicarbonate / 100) * results.weeklyNeeds.bicarbonate
        };

        // Start with current plan as base
        let newBottles = {};
        results.combination.plan.forEach(item => {
            if (!item.removed && availableProducts.find(p => p.name === item.name)) {
                newBottles[item.name] = item.bottles;
            }
        });

        // For each mineral that increased, calculate additional bottles needed
        const minerals = ['magnesium', 'calcium', 'sodium', 'potassium', 'bicarbonate'];
        
        minerals.forEach(mineral => {
            const currentCoverage = results.combination.coverage[mineral];
            const targetCoverage = newTargets[mineral];
            
            if (targetCoverage > currentCoverage) {
                // Need more of this mineral
                const additionalNeeded = requiredMinerals[mineral] - results.combination.totalProvided[mineral];
                
                if (additionalNeeded > 0) {
                    // Find best product for this mineral (considering health focus boosts)
                    let bestProduct = null;
                    let bestScore = -1;
                    
                    availableProducts.forEach(product => {
                        let score = (product[mineral] || 0) * product.bottleSize;
                        
                        // Apply health focus boosts
                        formData.healthFocus.forEach(focusId => {
                            const focus = healthFocusOptions.find(f => f.id === focusId);
                            if (focus && focus.boost) {
                                const boostKey = product.name === 'ROI' ? 'roi' : 
                                                product.name === 'Gerolsteiner' ? 'gerolsteiner' :
                                                product.name === 'Vichy Catalan' ? 'vichy' :
                                                product.name === 'Marzia' ? 'marzia' : null;
                                if (boostKey && focus.boost[boostKey]) {
                                    score *= (1 + focus.boost[boostKey] * 0.5);
                                }
                            }
                        });
                        
                        if (score > bestScore) {
                            bestScore = score;
                            bestProduct = product;
                        }
                    });
                    
                    if (bestProduct && bestScore > 0) {
                        const mineralPerBottle = (bestProduct[mineral] || 0) * bestProduct.bottleSize;
                        if (mineralPerBottle > 0) {
                            const extraBottles = Math.ceil(additionalNeeded / mineralPerBottle);
                            newBottles[bestProduct.name] = (newBottles[bestProduct.name] || 0) + extraBottles;
                        }
                    }
                }
            }
        });

        // Build the adjusted plan
        const adjustedWeeklyPlan = results.combination.plan.map(item => {
            if (item.removed) return item;
            return {
                ...item,
                bottles: newBottles[item.name] || item.bottles,
                adjusted: (newBottles[item.name] || item.bottles) !== item.bottles
            };
        });

        // Recalculate coverage with new bottle counts
        let newTotalProvided = {
            magnesium: 0, calcium: 0, sodium: 0, potassium: 0, bicarbonate: 0
        };

        adjustedWeeklyPlan.forEach(item => {
            if (!item.removed) {
                const product = products.find(p => p.name === item.name);
                if (product) {
                    const liters = item.bottles * product.bottleSize;
                    newTotalProvided.magnesium += product.magnesium * liters;
                    newTotalProvided.calcium += product.calcium * liters;
                    newTotalProvided.sodium += product.sodium * liters;
                    newTotalProvided.potassium += product.potassium * liters;
                    newTotalProvided.bicarbonate += product.bicarbonate * liters;
                }
            }
        });

        const newCoverage = {
            magnesium: Math.min(999, Math.round((newTotalProvided.magnesium / results.weeklyNeeds.magnesium) * 100)),
            calcium: Math.min(999, Math.round((newTotalProvided.calcium / results.weeklyNeeds.calcium) * 100)),
            sodium: Math.min(999, Math.round((newTotalProvided.sodium / results.weeklyNeeds.sodium) * 100)),
            potassium: Math.min(999, Math.round((newTotalProvided.potassium / results.weeklyNeeds.potassium) * 100)),
            bicarbonate: Math.min(999, Math.round((newTotalProvided.bicarbonate / results.weeklyNeeds.bicarbonate) * 100))
        };

        const totalBottles = adjustedWeeklyPlan.filter(p => !p.removed).reduce((sum, item) => sum + item.bottles, 0);
        const newAvgCoverage = Math.round(
            (newCoverage.magnesium + newCoverage.calcium + newCoverage.sodium + newCoverage.potassium + newCoverage.bicarbonate) / 5
        );

        setAdjustedPlan({
            plan: adjustedWeeklyPlan,
            coverage: newCoverage,
            totalProvided: newTotalProvided,
            avgCoverage: newAvgCoverage,
            totalBottles
        });
    };

    // Handle mineral target adjustment
    const adjustMineralTarget = (mineral, delta) => {
        if (!customTargets) return;
        
        const currentTarget = customTargets[mineral];
        const newTarget = Math.max(0, Math.min(200, currentTarget + delta));
        
        const newTargets = {
            ...customTargets,
            [mineral]: newTarget
        };
        
        setCustomTargets(newTargets);
        calculateAdjustedPlan(newTargets);
    };

    // Handle bottle count adjustment - NEW FEATURE
    const adjustBottleCount = (productName, delta) => {
        if (!results || !results.weeklyNeeds) return;
        
        // Get current plan (adjusted or original)
        const currentPlan = adjustedPlan ? adjustedPlan.plan : results.combination.plan;
        
        // Create new plan with adjusted bottle count
        const newPlan = currentPlan.map(item => {
            if (item.name === productName && !item.removed) {
                const newBottles = Math.max(0, item.bottles + delta);
                return { 
                    ...item, 
                    bottles: newBottles,
                    adjusted: true
                };
            }
            return item;
        });
        
        // Recalculate coverage based on new bottle counts
        let newTotalProvided = {
            magnesium: 0, calcium: 0, sodium: 0, potassium: 0, bicarbonate: 0
        };
        
        newPlan.forEach(item => {
            if (!item.removed && item.bottles > 0) {
                const product = products.find(p => p.name === item.name);
                if (product) {
                    const liters = item.bottles * product.bottleSize;
                    newTotalProvided.magnesium += product.magnesium * liters;
                    newTotalProvided.calcium += product.calcium * liters;
                    newTotalProvided.sodium += product.sodium * liters;
                    newTotalProvided.potassium += product.potassium * liters;
                    newTotalProvided.bicarbonate += product.bicarbonate * liters;
                }
            }
        });
        
        const newCoverage = {
            magnesium: Math.min(999, Math.round((newTotalProvided.magnesium / results.weeklyNeeds.magnesium) * 100)),
            calcium: Math.min(999, Math.round((newTotalProvided.calcium / results.weeklyNeeds.calcium) * 100)),
            sodium: Math.min(999, Math.round((newTotalProvided.sodium / results.weeklyNeeds.sodium) * 100)),
            potassium: Math.min(999, Math.round((newTotalProvided.potassium / results.weeklyNeeds.potassium) * 100)),
            bicarbonate: Math.min(999, Math.round((newTotalProvided.bicarbonate / results.weeklyNeeds.bicarbonate) * 100))
        };
        
        const totalBottles = newPlan.filter(p => !p.removed).reduce((sum, item) => sum + item.bottles, 0);
        const newAvgCoverage = Math.round(
            (newCoverage.magnesium + newCoverage.calcium + newCoverage.sodium + newCoverage.potassium + newCoverage.bicarbonate) / 5
        );
        
        // Update custom targets to reflect new coverage
        setCustomTargets(newCoverage);
        
        setAdjustedPlan({
            plan: newPlan,
            coverage: newCoverage,
            totalProvided: newTotalProvided,
            avgCoverage: newAvgCoverage,
            totalBottles
        });
    };

    // Reset to original recommendation
    const resetToOriginal = () => {
        if (results && results.combination) {
            setCustomTargets({ ...results.combination.coverage });
            setAdjustedPlan(null);
        }
    };

    const mineralLabels = {
        magnesium: t('magnesiumNeed'),
        calcium: t('calciumNeed'),
        sodium: t('sodiumNeed'),
        potassium: t('potassiumNeed'),
        bicarbonate: t('bicarbonateBenefit')
    };

    const focusLabels = {
        sleep: t('focusSleep'),
        stress: t('focusStress'),
        recovery: t('focusRecovery'),
        endurance: t('focusEndurance'),
        muscle: t('focusMuscle'),
        injury: t('focusInjury'),
        energy: t('focusEnergy'),
        digestion: t('focusDigestion'),
        concentration: t('focusConcentration'),
        boneHealth: t('focusBoneHealth')
    };

    return (
        <section className="py-32 bg-fjord text-white" data-testid="calculator-section">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="text-center mb-16"
                >
                    <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-4">
                        {t('calculatorTitle')}
                    </p>
                    <h2 className="font-display text-4xl md:text-5xl font-light mb-4">
                        {t('calculatorHeading')}
                    </h2>
                    <p className="text-white/70 max-w-xl mx-auto">
                        {t('calculatorDescription')}
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    {/* Calculator Form */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="space-y-6"
                    >
                        {/* Basic Info */}
                        <div className="bg-white/10 backdrop-blur-sm p-6 md:p-8">
                            <div className="grid grid-cols-2 gap-6 mb-6">
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                                        {t('age')}
                                    </Label>
                                    <Input
                                        type="number"
                                        value={formData.age}
                                        onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-none"
                                        placeholder="35"
                                        data-testid="calc-age"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                                        {t('weight')}
                                    </Label>
                                    <Input
                                        type="number"
                                        value={formData.weight}
                                        onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-none"
                                        placeholder="75"
                                        data-testid="calc-weight"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                                        {t('gender')}
                                    </Label>
                                    <Select
                                        value={formData.gender}
                                        onValueChange={(value) => setFormData({ ...formData, gender: value })}
                                    >
                                        <SelectTrigger className="bg-white/10 border-white/20 text-white rounded-none">
                                            <SelectValue placeholder={t('gender')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="male">{t('male')}</SelectItem>
                                            <SelectItem value="female">{t('female')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                                        {t('activityLevel')}
                                    </Label>
                                    <Select
                                        value={formData.activityLevel}
                                        onValueChange={(value) => setFormData({ ...formData, activityLevel: value })}
                                    >
                                        <SelectTrigger className="bg-white/10 border-white/20 text-white rounded-none">
                                            <SelectValue placeholder={t('activityLevel')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="sedentary">{t('sedentary')}</SelectItem>
                                            <SelectItem value="moderate">{t('moderate')}</SelectItem>
                                            <SelectItem value="active">{t('active')}</SelectItem>
                                            <SelectItem value="veryActive">{t('veryActive')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>

                        {/* Health Focus Options */}
                        <div className="bg-white/10 backdrop-blur-sm p-6 md:p-8">
                            <div className="flex items-center gap-2 mb-4">
                                <Target className="w-5 h-5 text-white/70" />
                                <h3 className="text-sm uppercase tracking-wider text-white/70">{t('healthFocus')}</h3>
                            </div>
                            <p className="text-xs text-white/50 mb-4">{t('healthFocusDescription')}</p>
                            
                            <div className="grid grid-cols-2 gap-3">
                                {Object.entries(focusLabels).map(([id, label]) => (
                                    <div 
                                        key={id}
                                        className={`flex items-center space-x-3 p-3 cursor-pointer transition-colors ${
                                            formData.healthFocus.includes(id) 
                                                ? 'bg-white/20' 
                                                : 'bg-white/5 hover:bg-white/10'
                                        }`}
                                        onClick={() => toggleHealthFocus(id)}
                                    >
                                        <Checkbox
                                            id={id}
                                            checked={formData.healthFocus.includes(id)}
                                            onCheckedChange={() => toggleHealthFocus(id)}
                                            className="border-white/40 data-[state=checked]:bg-white data-[state=checked]:text-fjord"
                                        />
                                        <label 
                                            htmlFor={id} 
                                            className="text-sm cursor-pointer flex-1"
                                        >
                                            {label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Low sodium + Calculate */}
                        <div className="bg-white/10 backdrop-blur-sm p-6 md:p-8">
                            <div className="flex items-center space-x-3 mb-6 p-3 bg-white/5">
                                <Checkbox
                                    id="lowSodium"
                                    checked={formData.lowSodium}
                                    onCheckedChange={(checked) => setFormData({ ...formData, lowSodium: checked })}
                                    className="border-white/40 data-[state=checked]:bg-white data-[state=checked]:text-fjord"
                                />
                                <div className="flex-1">
                                    <label htmlFor="lowSodium" className="text-sm font-medium cursor-pointer">
                                        {t('lowSodium')}
                                    </label>
                                    <p className="text-xs text-white/50">{t('lowSodiumDescription')}</p>
                                </div>
                            </div>

                            <Button
                                onClick={calculateNeeds}
                                className="w-full bg-white text-fjord hover:bg-white/90 rounded-none py-6 text-xs uppercase tracking-[0.15em] font-bold"
                            >
                                <Calculator className="w-4 h-4 mr-2" />
                                {t('calculate')}
                            </Button>
                        </div>
                    </motion.div>

                    {/* Results */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        {results ? (
                            <div className="space-y-6" data-testid="calc-results">
                                {/* Daily Needs */}
                                <div className="bg-white/10 backdrop-blur-sm p-6">
                                    <div className="mb-4">
                                        <h3 className="text-sm font-medium text-white mb-1">
                                            {t('basedOnProfile')}
                                        </h3>
                                        <p className="text-xs text-white/50">
                                            {t('basedOnProfileSubtitle')}
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        {Object.entries(results.dailyNeeds).map(([mineral, value]) => (
                                            <div key={mineral} className="flex justify-between bg-white/5 p-2">
                                                <span className="text-white/70">{mineralLabels[mineral]}</span>
                                                <span className="font-mono">{value} mg</span>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-xs text-white/40 mt-3">{t('source')}</p>
                                </div>

                                {/* Coverage - Interactive Adjustment */}
                                <div className="bg-white/10 backdrop-blur-sm p-6">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-display text-xl flex items-center gap-2">
                                            <Package className="w-5 h-5" />
                                            {language === 'no' ? 'Mineraldekning' : 'Mineral Coverage'}
                                        </h3>
                                        <span className="text-2xl font-display">
                                            {adjustedPlan ? adjustedPlan.avgCoverage : results.combination.avgCoverage}%
                                        </span>
                                    </div>
                                    
                                    {/* Adjustment mode indicator */}
                                    <div className="flex items-center justify-between mb-4">
                                        <p className="text-white/60 text-sm flex items-center gap-2">
                                            <Sliders className="w-4 h-4" />
                                            {language === 'no' 
                                                ? 'Juster dekning med +/- knappene' 
                                                : 'Adjust coverage with +/- buttons'}
                                        </p>
                                        {adjustedPlan && (
                                            <button
                                                onClick={resetToOriginal}
                                                className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"
                                            >
                                                <RotateCcw className="w-3 h-3" />
                                                {language === 'no' ? 'Tilbakestill' : 'Reset'}
                                            </button>
                                        )}
                                    </div>
                                    
                                    <div className="space-y-3">
                                        {Object.entries(customTargets || results.combination.coverage).map(([mineral, targetPercent]) => {
                                            const originalPercent = results.combination.coverage[mineral];
                                            const currentPercent = adjustedPlan ? adjustedPlan.coverage[mineral] : originalPercent;
                                            const isAdjusted = adjustedPlan && currentPercent !== originalPercent;
                                            
                                            return (
                                                <div key={mineral} className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-white/60 w-24">{mineralLabels[mineral]}</span>
                                                        
                                                        {/* Progress bar */}
                                                        <div className="flex-1 h-3 bg-white/20 rounded-full overflow-hidden relative">
                                                            {/* Original coverage indicator */}
                                                            {isAdjusted && (
                                                                <div 
                                                                    className="absolute h-full bg-white/30 rounded-full"
                                                                    style={{ width: `${Math.min(100, originalPercent)}%` }}
                                                                />
                                                            )}
                                                            {/* Current/Target coverage */}
                                                            <div 
                                                                className={`h-full rounded-full transition-all duration-300 ${
                                                                    isAdjusted ? 'bg-amber-400' : 'bg-white'
                                                                }`}
                                                                style={{ width: `${Math.min(100, currentPercent)}%` }}
                                                            />
                                                        </div>
                                                        
                                                        {/* Percentage display */}
                                                        <span className={`text-xs font-mono w-14 text-right ${
                                                            isAdjusted ? 'text-amber-400' : ''
                                                        }`}>
                                                            {currentPercent}%
                                                        </span>
                                                        
                                                        {/* +/- buttons */}
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                onClick={() => adjustMineralTarget(mineral, -10)}
                                                                className="w-6 h-6 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded transition-colors"
                                                                title={language === 'no' ? 'Reduser mål' : 'Decrease target'}
                                                            >
                                                                <Minus className="w-3 h-3" />
                                                            </button>
                                                            <button
                                                                onClick={() => adjustMineralTarget(mineral, 10)}
                                                                className="w-6 h-6 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded transition-colors"
                                                                title={language === 'no' ? 'Øk mål' : 'Increase target'}
                                                            >
                                                                <Plus className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    
                                    {/* Adjustment summary */}
                                    {adjustedPlan && (
                                        <div className="mt-4 pt-4 border-t border-white/20">
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-amber-400">
                                                    {language === 'no' ? 'Justert anbefaling' : 'Adjusted recommendation'}
                                                </span>
                                                <span className="text-white/60">
                                                    {language === 'no' 
                                                        ? `${adjustedPlan.totalBottles} flasker/uke`
                                                        : `${adjustedPlan.totalBottles} bottles/week`}
                                                </span>
                                            </div>
                                            {adjustedPlan.totalBottles > 14 && (
                                                <p className="text-xs text-amber-400/70 mt-2">
                                                    {language === 'no'
                                                        ? '⚠️ Høyt antall flasker. Vurder kosttilskudd eller matkilder for å dekke behovet.'
                                                        : '⚠️ High bottle count. Consider supplements or food sources to meet needs.'}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Weekly Plan */}
                                <div className="bg-white/10 backdrop-blur-sm p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-sm uppercase tracking-wider text-white/60">
                                            {adjustedPlan 
                                                ? (language === 'no' ? 'Justert ukesplan' : 'Adjusted Weekly Plan')
                                                : t('weeklyPlan')}
                                        </h3>
                                        {(excludedProducts.length > 0 || adjustedPlan) && (
                                            <button
                                                onClick={() => {
                                                    setExcludedProducts([]);
                                                    resetToOriginal();
                                                    calculateNeeds();
                                                }}
                                                className="flex items-center gap-1 text-xs text-white/50 hover:text-white transition-colors"
                                            >
                                                <RotateCcw className="w-3 h-3" />
                                                {language === 'no' ? 'Tilbakestill' : 'Reset'}
                                            </button>
                                        )}
                                    </div>
                                    <div className="space-y-3">
                                        {(adjustedPlan ? adjustedPlan.plan : results.combination.plan).map((item) => {
                                            const originalItem = results.combination.plan.find(p => p.name === item.name);
                                            const isAdjusted = adjustedPlan && originalItem && item.bottles !== originalItem.bottles;
                                            const isIncreased = isAdjusted && item.bottles > originalItem.bottles;
                                            const isDecreased = isAdjusted && item.bottles < originalItem.bottles;
                                            
                                            return (
                                                <div 
                                                    key={item.name}
                                                    className={`flex items-center gap-3 p-3 transition-colors ${
                                                        item.removed 
                                                            ? 'bg-white/5 opacity-40' 
                                                            : isIncreased
                                                                ? 'bg-amber-500/20 border border-amber-500/30'
                                                                : isDecreased
                                                                    ? 'bg-blue-500/20 border border-blue-500/30'
                                                                    : 'bg-white/5 hover:bg-white/10'
                                                    }`}
                                                >
                                                <div className="w-10 h-14 bg-white/10 overflow-hidden flex-shrink-0 flex items-center justify-center">
                                                    <img 
                                                        src={item.image} 
                                                        alt={item.name}
                                                        className="h-full w-auto object-contain"
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className={`font-display text-sm ${item.removed ? 'line-through' : ''}`}>{item.name}</h4>
                                                    <span className="text-xs text-white/50">{item.role[language] || item.role.no}</span>
                                                </div>
                                                
                                                {/* Bottle count with +/- controls */}
                                                {!item.removed && (
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => adjustBottleCount(item.name, -1)}
                                                            className="w-7 h-7 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded transition-colors"
                                                            title={language === 'no' ? 'Reduser antall' : 'Decrease count'}
                                                        >
                                                            <Minus className="w-3 h-3" />
                                                        </button>
                                                        
                                                        <div className="flex flex-col items-center min-w-[40px]">
                                                            {isAdjusted && originalItem && (
                                                                <span className="text-[10px] text-white/40 line-through">{originalItem.bottles}</span>
                                                            )}
                                                            <span className={`text-lg font-mono leading-none ${
                                                                isIncreased ? 'text-amber-400' : isDecreased ? 'text-blue-400' : ''
                                                            }`}>
                                                                {item.bottles}
                                                            </span>
                                                        </div>
                                                        
                                                        <button
                                                            onClick={() => adjustBottleCount(item.name, 1)}
                                                            className="w-7 h-7 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded transition-colors"
                                                            title={language === 'no' ? 'Øk antall' : 'Increase count'}
                                                        >
                                                            <Plus className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                )}
                                                
                                                {item.removed && (
                                                    <button
                                                        onClick={() => {
                                                            setExcludedProducts([]);
                                                            resetToOriginal();
                                                            calculateNeeds();
                                                        }}
                                                        className="p-2 hover:bg-white/10 transition-colors rounded"
                                                        title={language === 'no' ? 'Legg til igjen' : 'Add back'}
                                                    >
                                                        <RotateCcw className="w-4 h-4 text-white/50 hover:text-white" />
                                                    </button>
                                                )}
                                            </div>
                                        );
                                        })}
                                    </div>
                                    
                                    {/* Instruction text */}
                                    <p className="text-xs text-white/40 mt-3 flex items-center gap-1">
                                        <Sliders className="w-3 h-3" />
                                        {language === 'no' 
                                            ? 'Bruk +/- for å se hvordan mineraldekningen endres'
                                            : 'Use +/- to see how mineral coverage changes'}
                                    </p>
                                    
                                    <div className="mt-4 pt-4 border-t border-white/20 flex justify-between items-center">
                                        <span className="text-white/60">
                                            {language === 'no' 
                                                ? `Totalt ${adjustedPlan ? adjustedPlan.totalBottles : results.combination.totalBottles} flasker/uke`
                                                : `Total ${adjustedPlan ? adjustedPlan.totalBottles : results.combination.totalBottles} bottles/week`
                                            }
                                        </span>
                                        {excludedProducts.length > 0 && (
                                            <span className="text-xs text-amber-400">
                                                {language === 'no' 
                                                    ? `${excludedProducts.length} produkt fjernet`
                                                    : `${excludedProducts.length} product removed`
                                                }
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* CTA Section - Recommendation & Actions */}
                                <div className="bg-gradient-to-br from-white/20 to-white/10 backdrop-blur-sm p-6 border border-white/20">
                                    <h3 className="font-display text-xl mb-3">
                                        {language === 'no' ? 'Anbefalt hydreringsplan' : 'Recommended Hydration Plan'}
                                    </h3>
                                    
                                    {/* Personalized result description based on selections */}
                                    <div className="text-white/80 text-sm mb-4 space-y-2">
                                        {(() => {
                                            const activityLabels = {
                                                sedentary: { no: 'stillesittende', en: 'sedentary' },
                                                moderate: { no: 'moderat aktiv', en: 'moderately active' },
                                                active: { no: 'aktiv', en: 'active' },
                                                veryActive: { no: 'svært aktiv', en: 'very active' }
                                            };
                                            const focusLabels = {
                                                sleep: { no: 'søvnkvalitet', en: 'sleep quality' },
                                                stress: { no: 'stressmestring', en: 'stress management' },
                                                recovery: { no: 'restitusjon', en: 'recovery' },
                                                endurance: { no: 'utholdenhet', en: 'endurance' },
                                                muscle: { no: 'muskelfunksjon', en: 'muscle function' },
                                                injury: { no: 'skadeforebygging', en: 'injury prevention' },
                                                energy: { no: 'energinivå', en: 'energy levels' },
                                                digestion: { no: 'fordøyelse', en: 'digestion' },
                                                concentration: { no: 'konsentrasjon', en: 'concentration' },
                                                boneHealth: { no: 'benhelse', en: 'bone health' }
                                            };
                                            
                                            const activityText = activityLabels[formData.activityLevel]?.[language] || formData.activityLevel;
                                            const selectedFocuses = formData.healthFocus.map(f => focusLabels[f]?.[language] || f);
                                            
                                            if (language === 'no') {
                                                return (
                                                    <>
                                                        <p>
                                                            Med din <strong>{activityText}</strong> livsstil
                                                            {selectedFocuses.length > 0 && (
                                                                <> og fokus på <strong>{selectedFocuses.join(', ')}</strong></>
                                                            )}
                                                            , har vi satt sammen en hydreringsplan tilpasset dine behov.
                                                        </p>
                                                        <p>
                                                            Denne planen bidrar til optimal mineralbalanse basert på dine valg.
                                                        </p>
                                                    </>
                                                );
                                            } else {
                                                return (
                                                    <>
                                                        <p>
                                                            With your <strong>{activityText}</strong> lifestyle
                                                            {selectedFocuses.length > 0 && (
                                                                <> and focus on <strong>{selectedFocuses.join(', ')}</strong></>
                                                            )}
                                                            , we have put together a hydration plan tailored to your needs.
                                                        </p>
                                                        <p>
                                                            This plan contributes to optimal mineral balance based on your choices.
                                                        </p>
                                                    </>
                                                );
                                            }
                                        })()}
                                    </div>
                                    
                                    <p className="text-white/50 text-xs mb-6 italic">
                                        {language === 'no' 
                                            ? 'Et variert og balansert kosthold er viktig for god helse. Naturlig mineralvann kan være en del av dette, men bør ikke betraktes som en erstatning for et variert kosthold.'
                                            : 'A varied and balanced diet is important for good health. Natural mineral water can be part of this, but should not be considered a substitute for a varied diet.'
                                        }
                                    </p>

                                    {/* Recommended Products */}
                                    <div className="flex gap-3 mb-6 overflow-x-auto pb-2">
                                        {results.combination.plan.filter(item => !item.removed && item.bottles > 0).map((item) => {
                                            const product = products.find(p => p.name === item.name);
                                            return (
                                                <Link
                                                    key={item.name}
                                                    to={`/products/${product?.productId || item.name.toLowerCase()}`}
                                                    className="flex-shrink-0 bg-white/10 hover:bg-white/20 transition-colors p-3 flex flex-col items-center gap-2 min-w-[100px]"
                                                >
                                                    <div className="w-12 h-16 flex items-center justify-center">
                                                        <img 
                                                            src={item.image} 
                                                            alt={item.name}
                                                            className="h-full w-auto object-contain"
                                                        />
                                                    </div>
                                                    <span className="text-xs font-medium">{item.name}</span>
                                                    <span className="text-[10px] text-white/50">{item.bottles} {language === 'no' ? 'per uke' : 'per week'}</span>
                                                </Link>
                                            );
                                        })}
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="space-y-3">
                                        <a
                                            href="mailto:hello@rawminerals.no?subject=Mineralvann%20forespørsel&body=Hei!%0A%0AJeg%20har%20brukt%20mineralkalkulatoren%20og%20ønsker%20å%20bestille%20mineralvann.%0A%0AMed%20vennlig%20hilsen"
                                            className="w-full flex items-center justify-center gap-2 bg-white text-fjord hover:bg-white/90 py-4 text-xs uppercase tracking-[0.15em] font-bold transition-colors"
                                        >
                                            <Mail className="w-4 h-4" />
                                            {language === 'no' ? 'Kontakt oss for bestilling' : 'Contact us to order'}
                                        </a>
                                        
                                        <a
                                            href="tel:+4797499879"
                                            className="w-full flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 py-4 text-xs uppercase tracking-[0.15em] font-medium transition-colors"
                                        >
                                            <Phone className="w-4 h-4" />
                                            {language === 'no' ? 'Ring oss: +47 974 99 879' : 'Call us: +47 974 99 879'}
                                        </a>

                                        <button
                                            onClick={() => {
                                                const text = language === 'no'
                                                    ? `Visste du at ulike mineralvann har helt forskjellig mineralsammensetning? Jeg har akkurat oppdaget mitt mineralbehov med Raw Minerals sin kalkulator! Ta testen selv på rawminerals.no`
                                                    : `Did you know that different mineral waters have completely different mineral compositions? I just discovered my mineral needs with the Raw Minerals calculator! Take the test yourself at rawminerals.no`;
                                                
                                                if (navigator.share) {
                                                    navigator.share({
                                                        title: language === 'no' ? 'Raw Minerals - Mineralkalkulatoren' : 'Raw Minerals - Mineral Calculator',
                                                        text: text,
                                                        url: 'https://www.rawminerals.no'
                                                    });
                                                } else {
                                                    navigator.clipboard.writeText(text + ' https://www.rawminerals.no');
                                                    toast.success(language === 'no' ? 'Kopiert til utklippstavle!' : 'Copied to clipboard!');
                                                }
                                            }}
                                            className="w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 py-4 text-xs uppercase tracking-[0.15em] font-medium transition-colors"
                                        >
                                            <Share2 className="w-4 h-4" />
                                            {language === 'no' ? 'Del kalkulatoren' : 'Share calculator'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="h-full flex items-center justify-center text-center p-12 bg-white/5">
                                <div>
                                    <Droplets className="w-16 h-16 mx-auto text-white/30 mb-6" />
                                    <p className="text-white/50 max-w-sm">
                                        {t('calculatorDescription')}
                                    </p>
                                </div>
                            </div>
                        )}
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default MineralCalculator;
